import { useEffect } from 'react';
import { useFormik } from 'formik';
import RenderIf from "../render-if/Renderif";
import XInput from '../common/XInput';
import XModal from '../common/XModal';
import { getDefaultClient } from '../../utils/formSchema';
import { generateRandomIds, getIsShowCreditInfo } from '../../utils/helper';
import { DefaultMultiPlaintiffName } from "../../utils/constant";
import { formTitleClasses } from "../common/formClasses";
import InfoText from './InfoText';
import * as Yup from "yup";
import { useProductContext } from '../../contexts/ProductContext';
import AddressForm from '../common/AddressForm';

const defaultClient = getDefaultClient();

const insuranceFields = [
    ["insurance.insuranceName", "Insurance Name"],
    ["insurance.claimNumber", "Claim Number"],
    ["insurance.adjuster.name", "Adjuster Name"],
    ["insurance.adjuster.email", "Adjuster Email"],
    ["insurance.adjuster.phoneNumber", "Adjuster Phone Number"],
];

const insuranceWrapperClasses = "mb-10!";
const insuranceTitleClasses = `${formTitleClasses} mb-6`;
const lastFormGroupClasses = "form-group mb-0!";
const gridClasses = "grid grid-cols-1 md:grid-cols-2 gap-x-6";

const PlaintiffModal = ({
    show,
    onClose,
    onSave,
    formik: parentFormik,
    selectedTiffIndex,
    updatePlainTiff,
    addNewPlainTiff
}) => {
    useEffect(() => {
        if (show) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => { document.body.style.overflow = "auto"; };
    }, [show]);

    const currentClients = parentFormik.values.clients;
    const isEditMode = selectedTiffIndex < currentClients.length;
    const showCreditInfo = getIsShowCreditInfo(selectedTiffIndex, parentFormik);

    const isReplacingDefault = (
        currentClients.length === 1 &&
        (!currentClients[0]?.personalInfo?.clientName ||
            currentClients[0]?.personalInfo?.clientName === DefaultMultiPlaintiffName.DEFAULT_PLAINTIFF_Name)
    );

    const localFormik = useFormik({
        initialValues: isEditMode ? currentClients[selectedTiffIndex] : defaultClient,
        enableReinitialize: true,
        validationSchema: Yup.object({
            personalInfo: Yup.object({
                clientName: Yup.string().trim().required("Client Name is required"),
            }),
        }),
        onSubmit: (values) => {
            if (isReplacingDefault) {
                values.clientId = currentClients[0].clientId;
                addNewPlainTiff?.(values);
            } else if (isEditMode) {
                updatePlainTiff?.({ clientObj: values, index: selectedTiffIndex });
            } else {
                values.clientId = generateRandomIds();
                addNewPlainTiff?.(values);
            }

            onSave?.();
        },
    });

    const getFieldValue = (path) => path.split('.').reduce((acc, key) => acc?.[key], localFormik.values) || '';

    const handleFieldChange = (path, value) => localFormik.setFieldValue(path, value);

    const { isPFASType } = useProductContext();

    const isDefaultCaseType = parentFormik.values.caseInfo.caseType === "AUTO_ACCIDENT" || (isPFASType && isPFASType(parentFormik.values.caseInfo.caseType));

    return (
        <XModal
            isOpen={show}
            title="Add Plaintiff Details"
            onClose={onClose}
            onSubmit={localFormik.handleSubmit}
            submitText="Save"
            size="lg"
        >
            <div className={gridClasses}>
                <div className="mb-4">
                    <XInput
                        label="Client Name"
                        name="personalInfo.clientName"
                        value={getFieldValue('personalInfo.clientName')}
                        onChange={(e) => handleFieldChange('personalInfo.clientName', e.target.value)}
                        error={localFormik.touched.personalInfo?.clientName && localFormik.errors.personalInfo?.clientName}
                    />
                </div>
                <div className="mb-4">
                    <XInput
                        label="Phone Number"
                        name="personalInfo.phoneNumber"
                        value={getFieldValue('personalInfo.phoneNumber')}
                        onChange={(e) => handleFieldChange('personalInfo.phoneNumber', e.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <XInput
                        label="Email"
                        name="personalInfo.email"
                        value={getFieldValue('personalInfo.email')}
                        onChange={(e) => handleFieldChange('personalInfo.email', e.target.value)}
                    />
                </div>
                <div className={lastFormGroupClasses}>
                    <XInput
                        label="SSN"
                        name="personalInfo.ssn"
                        value={getFieldValue('personalInfo.ssn')}
                        onChange={(e) => handleFieldChange('personalInfo.ssn', e.target.value)}
                    />
                </div>
            </div>
            <RenderIf shouldRender={isDefaultCaseType}>
                <div className={insuranceWrapperClasses}>
                    <h2 className={insuranceTitleClasses}>Insurance Details</h2>
                    <div className={gridClasses}>
                        {insuranceFields.map(([path, label]) => (
                            <div className="mb-4" key={path}>
                                    <XInput
                                        label={label}
                                        name={path}
                                        value={getFieldValue(path)}
                                        onChange={(e) => handleFieldChange(path, e.target.value)}
                                    />
                            </div>
                        ))}
                    </div>
                    <AddressForm fieldPath={`insurance.adjuster`} legend="Adjuster Address" formik={localFormik} />
                </div>
            </RenderIf>
            {showCreditInfo && <InfoText message="Cases are charged for one credit per plaintiff" />}
        </XModal>
    );
};

export default PlaintiffModal;
